import { useMemo, useState } from "react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useRanking } from "@/modules/leagues/hooks/useRanking";
import { usePodiums, useLatestScoredRound } from "@/modules/leagues/hooks/usePodiums";
import { useAvailableRounds } from "@/modules/leagues/hooks/useMatches";
import { useLiveRankingBoost } from "@/modules/leagues/hooks/useLiveRankingBoost";
import { RankingRowItem } from "@/modules/leagues/components/RankingRowItem";
import { Skeleton } from "@/components/atoms/Skeleton";
import { ErrorState } from "@/components/organisms/ErrorState";
import { EmptyState } from "@/components/organisms/EmptyState";
import { TrophyIllustration } from "@/components/atoms/illustrations";
import { Medal, Share2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/atoms/Avatar";
import { Button } from "@/components/atoms/Button";
import { cn } from "@/utils/cn";
import { shareRanking } from "@/modules/leagues/utils/shareRanking";
import { toast } from "@/hooks/use-toast";
import type { RankingScope } from "@/modules/leagues/types/ranking";

const SCOPES: Array<{ key: RankingScope | "podios"; label: string }> = [
  { key: "geral", label: "Geral" },
  { key: "rodada", label: "Rodada" },
  { key: "mensal", label: "Mês" },
  { key: "podios", label: "Pódios" },
];

const MONTH_ABBREV = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** "2026-08" → "Ago 2026" (pedido do usuário — nada de mostrar o formato cru "AAAA-MM"). */
function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  const abbrev = MONTH_ABBREV[(month ?? 1) - 1] ?? monthKey;
  return `${abbrev} ${year}`;
}

function monthOptions(): string[] {
  const now = new Date();
  const months: string[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(d.toISOString().slice(0, 7));
  }
  return months;
}

function initials(nome: string) {
  return nome.split(" ").slice(0, 2).map((n) => n[0]).join("");
}

/**
 * Ranking da liga — seção PRÓPRIA (item 19), não mais dentro de
 * Estatísticas. Geral/Rodada/Mês usam dado real gravado pelo cron
 * (pontos, pontosPorRodada, pontosPorMes) — fonte única de verdade
 * (item 36), nunca recalculado no navegador.
 */
export function LeagueRankingPage() {
  const { league } = useLeagueContext();
  const [scope, setScope] = useState<RankingScope | "podios">("geral");
  const { data: rounds } = useAvailableRounds(league.id);
  const [selectedRound, setSelectedRound] = useState<number | undefined>(undefined);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Rodada padrão da aba Ranking: a ÚLTIMA que já tem pontuação real
  // registrada — NUNCA a "próxima a acontecer" (essa é a lógica certa da
  // aba Palpites, mas aqui sempre mostraria 0, mesmo com a rodada anterior
  // já toda pontuada — bug relatado: "rodada 22 já passou e pontuada,
  // mas aparece zero", porque a tela estava mostrando a rodada 23 por
  // engano).
  const { data: latestScoredRound, isLoading: loadingLatestRound } = useLatestScoredRound(league.id);
  const effectiveRound = selectedRound ?? latestScoredRound ?? rounds?.[rounds.length - 1]?.round;

  const roundOrMonth = scope === "rodada" ? effectiveRound : scope === "mensal" ? selectedMonth : undefined;
  const { data: ranking, isLoading: loadingRanking, isError, refetch } = useRanking(
    league.id,
    scope === "podios" ? "geral" : scope,
    roundOrMonth
  );
  // Enquanto ainda não sabemos QUAL rodada mostrar (aguardando
  // useLatestScoredRound resolver), a busca do ranking fica
  // propositalmente desligada (ver useRanking) — sem isto aqui, a tela
  // ficava em branco nesse intervalo (nem carregando, nem erro, nem
  // dado) — bug relatado: "às vezes não carrega nada".
  const isLoading = loadingRanking || (scope === "rodada" && effectiveRound === undefined && loadingLatestRound);
  const { data: podiums, isLoading: loadingPodiums } = usePodiums(league.id);
  const { data: liveBoost } = useLiveRankingBoost(league.id);

  // Enquanto a partida está genuinamente AO VIVO, soma o provisório em
  // cima de `pontos` e reordena — assim que ela termina (status deixa de
  // ser "live"), o filtro dentro de useLiveRankingBoost já exclui essa
  // partida sozinho, e o Ranking volta a mostrar só o valor oficial,
  // parado, até o cron validar (regra explícita do usuário).
  const rankingWithLiveBoost = useMemo(() => {
    if (!ranking) return ranking;
    if (!liveBoost) return ranking.map((row) => ({ row, boost: 0 }));
    return ranking
      .map((row) => {
        let boost = 0;
        if (scope === "geral") boost = liveBoost.totalByUid[row.uid] ?? 0;
        else if (scope === "rodada" && effectiveRound !== undefined) {
          boost = liveBoost.byUidAndRound[row.uid]?.[effectiveRound] ?? 0;
        } else if (scope === "mensal") {
          boost = liveBoost.byUidAndMonth[row.uid]?.[selectedMonth] ?? 0;
        }
        return { row: { ...row, pontos: row.pontos + boost }, boost };
      })
      .sort((a, b) => b.row.pontos - a.row.pontos)
      .map((entry, idx) => ({ row: { ...entry.row, posicao: idx + 1 }, boost: entry.boost }));
  }, [ranking, liveBoost, scope, effectiveRound, selectedMonth]);

  async function handleShareRanking() {
    if (!rankingWithLiveBoost || rankingWithLiveBoost.length === 0) return;
    const rowsToShare = rankingWithLiveBoost.map((entry) => entry.row);
    const scopeTitle =
      scope === "geral"
        ? "Ranking Geral"
        : scope === "rodada"
          ? (rounds?.find((r) => r.round === effectiveRound)?.label ?? `Rodada ${effectiveRound ?? ""}`)
          : `Ranking de ${formatMonthLabel(selectedMonth)}`;
    const result = await shareRanking(rowsToShare, scopeTitle, league.nome);
    if (result === "copied") toast({ variant: "success", title: "Texto do ranking copiado" });
    else if (result === "failed") toast({ variant: "destructive", title: "Não foi possível compartilhar" });
  }
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            onClick={() => setScope(s.key)}
            className={cn(
              "flex-1 whitespace-nowrap rounded-lg py-1.5 px-2 text-xs font-semibold font-display transition-colors",
              scope === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {scope === "rodada" && rounds && rounds.length > 0 && (
        <select
          value={effectiveRound}
          onChange={(e) => setSelectedRound(Number(e.target.value))}
          className="flex h-10 w-full rounded-xl border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {rounds.map((r) => (
            <option key={r.round} value={r.round}>
              {r.label}
            </option>
          ))}
        </select>
      )}

      {scope === "mensal" && (
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="flex h-10 w-full rounded-xl border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {monthOptions().map((m) => (
            <option key={m} value={m}>
              {formatMonthLabel(m)}
            </option>
          ))}
        </select>
      )}

      {scope !== "podios" && (
        <>
          {isLoading && (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          )}
          {isError && !ranking && (
            <ErrorState title="Não foi possível carregar o ranking" description="Verifique sua conexão e tente novamente." onRetry={() => refetch()} />
          )}
          {isError && ranking && (
            <p className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-center text-xs text-amber-800 dark:text-warning">
              Não deu pra atualizar agora — mostrando os últimos dados carregados.
            </p>
          )}
          {ranking && ranking.length === 0 && (
            <EmptyState illustration={TrophyIllustration} title="Ainda sem pontuação" description="Assim que as partidas forem pontuadas, o ranking aparece aqui." />
          )}
          {ranking && ranking.length > 0 && rankingWithLiveBoost && (
            <>
              <div className="space-y-2">
                {rankingWithLiveBoost.map(({ row, boost }) => (
                  <RankingRowItem key={row.uid} row={row} liveBoost={boost} />
                ))}
              </div>
              <Button variant="outline" className="w-full" onClick={handleShareRanking}>
                <Share2 className="h-4 w-4" /> Compartilhar ranking
              </Button>
            </>
          )}
        </>
      )}

      {scope === "podios" && (
        <>
          {loadingPodiums && <Skeleton className="h-40 w-full" />}
          {podiums && podiums.length === 0 && (
            <EmptyState illustration={TrophyIllustration} title="Nenhuma rodada pontuada ainda" description="Os pódios de cada rodada aparecem aqui assim que houver resultado." />
          )}
          {podiums && podiums.length > 0 && (
            <div className="space-y-4">
              {[...podiums].reverse().map((podium) => (
                <div key={podium.round} className="rounded-2xl border border-border bg-card p-3">
                  <p className="mb-2 font-display text-sm font-semibold">Rodada {podium.round}</p>
                  <div className="space-y-1.5">
                    {podium.top3.map((row) => (
                      <div key={row.uid} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
                        <span
                          className={cn(
                            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-display text-xs font-bold",
                            row.posicao === 1
                              ? "bg-accent-100 text-accent-700 dark:bg-accent-500/20 dark:text-accent-300"
                              : row.posicao === 2
                                ? "bg-muted text-muted-foreground"
                                : "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-400"
                          )}
                        >
                          {row.posicao}°
                        </span>
                        <Medal
                          className={cn(
                            "h-4 w-4 shrink-0",
                            row.posicao === 1 ? "text-accent-600 dark:text-accent-300" : row.posicao === 2 ? "text-muted-foreground" : "text-amber-700 dark:text-amber-400"
                          )}
                        />
                        <Avatar size="sm">
                          <AvatarImage src={row.avatarUrl ?? undefined} alt={row.nome} />
                          <AvatarFallback>{initials(row.nome)}</AvatarFallback>
                        </Avatar>
                        <span className="flex-1 truncate text-sm">{row.nome}</span>
                        <span className="font-display text-sm font-bold tabular-nums">{row.pontos} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
